import { action, flow, makeObservable, observable } from 'mobx';
import moment from 'moment';

import type { TDictObject } from 'src/api/directories/types';
import { hasValue } from 'src/shared/utils/common';
import { convertHoursToSeconds, convertSecondsToHours } from 'src/shared/utils/date';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import type { RootStore } from 'src/store';
import type { Directories } from 'src/store/directories/directories.store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import { calculateDurationByCommercialSpeed } from '../utils';

import type { FormStore } from './form.entity';
import type { RigOperationStage } from './rig-operations/rig-operation-stage.entity';
import type { RigOperation } from './rig-operations/rig-operation.entity';

export class FormDirectoryValuesService {
  private readonly form: FormStore;
  private readonly rootStore: RootStore;
  private readonly directories: Directories;
  private readonly notificationsService: NotificationsStore;
  readonly directoryValues = observable.map<string | number, Record<string, unknown>>();

  constructor(form: FormStore, rootStore: RootStore) {
    this.form = form;
    this.rootStore = rootStore;
    this.directories = rootStore.directories;
    this.notificationsService = rootStore.notifications;

    makeObservable(this);
  }

  @flow.bound
  private async *loadPassingValueBySectionsDirectory() {
    if (!this.form) {
      return;
    }

    const { rigOperationsService } = this.form;
    const { block, field, fluidType, wellType, wellPurpose } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value) {
      return;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    await this.rootStore.directories.loadObjects([
      [
        'Common_WellPassingBySection',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          wellPurposeId: wellPurpose.value,
          // reservoirIds: reservoir.value,
          year,
        },
      ],
    ]);
    yield;
  }

  @flow.bound
  async *loadWellPassingValueDirectory() {
    if (!this.form) {
      return;
    }

    const { rigOperationsService } = this.form;
    const { block, field, fluidType, wellType } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value) {
      return;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    await this.rootStore.directories.loadObjects([
      [
        'Common_WellPassing',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          year,
          // reservoirIds: reservoir.value,
        },
      ],
    ]);
    yield;
  }

  @flow.bound
  async *loadCommercialSpeedDirectory(rigOperation: RigOperation, stage: RigOperationStage) {
    if (!hasValue(rigOperation.rigId) || !hasValue(stage.startDate)) {
      return;
    }

    const { block, field, fluidType, wellType } = this.form.fields;
    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value) {
      return;
    }

    const modelId = this.rootStore.directories.getObject('Common_Rig')?.find((rig) => rig.id === rigOperation.rigId)
      ?.data['modelId'];

    if (!modelId) {
      return;
    }

    const year = moment(stage.startDate * 1000).year();

    await this.rootStore.directories.loadObjects([
      [
        'Common_CommercialSpeed',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          modelId,
          year,
          // reservoirIds: reservoir.value,
        },
      ],
    ]);
    yield;
  }

  // TODO: убрать костыль showNotifications
  getCommercialSpeedFromDirectories(rigId: number, startDate: number, showNotifications?: boolean): number | null {
    const { block, field, fluidType, wellType, wellPurpose, reservoir } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value) {
      return null;
    }

    const modelId = this.directories.getObject('Common_Rig')?.find((rig) => rig.id === rigId)?.data['modelId'];

    if (!modelId) {
      return null;
    }

    const year = moment(startDate * 1000).year();

    const filterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      modelId,
      year,
    };

    const commercialSpeedDirectories = this.directories
      .getObject('Common_CommercialSpeed', true, filterData)
      ?.filter((dv) => {
        const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
        const reservoirIds = reservoir.value as number[];

        if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
          return false;
        }

        return reservoirIds.every((v) => dvReservoirIds.includes(v));
      });

    if (!commercialSpeedDirectories || !commercialSpeedDirectories.length) {
      console.error('Не удалось найти справочники коммерческой скорости по заданным параметрам.');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT(
          'Не удалось найти справочники коммерческой скорости по заданным параметрам.',
        );
      }

      return null;
    }

    // TODO: localization
    if (commercialSpeedDirectories.length > 1) {
      if (showNotifications) {
        this.notificationsService.showErrorMessageT(
          'Найдено несколько актуальных справочников коммерческой скорости по заданным параметрам. Необходимо отключить неактуальные справочники. Коммерческая скорость не будет рассчитана.',
        );
      }

      console.error('Found multiple active Common_CommercialSpeed directories.');
      return null;
    }

    const commercialSpeedDirectory = commercialSpeedDirectories[0];

    const commercialSpeedByWellPurpose = commercialSpeedDirectory.data['commercialSpeedByWellPurpose'];

    if (!Array.isArray(commercialSpeedByWellPurpose)) {
      console.error('Возникла ошибка при расчёте коммерческой скорости');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT('Возникла ошибка при расчёте коммерческой скорости');
      }

      return null;
    }

    const commercialSpeed = commercialSpeedByWellPurpose.find(
      (commercialSpeedObject) =>
        parsePrimitivesFromString(commercialSpeedObject['wellPurposeId']) === wellPurpose.value,
    )?.['commercialSpeed'];

    if (!hasValue(commercialSpeed) || typeof commercialSpeed !== 'number') {
      console.error('Не удалось найти справочное значение коммерческой скорости по заданным параметрам.');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT(
          'Не удалось найти справочное значение коммерческой скорости по заданным параметрам.',
        );
      }

      return null;
    }

    return commercialSpeed;
  }

  // TODO: убрать костыль showNotifications
  getDirPassingValue(startDate: number, showNotifications?: boolean): number | null {
    const { block, field, fluidType, wellType, wellPurpose, reservoir } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value || !startDate) {
      return null;
    }

    const year = moment(startDate * 1000).year();

    const wellPassingFilterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      year,
    };

    const wellPassingDirectories = this.directories
      .getObject('Common_WellPassing', true, wellPassingFilterData)
      ?.filter((dv) => {
        const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
        const reservoirIds = reservoir.value as number[];

        if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
          return false;
        }

        return reservoirIds.every((v) => dvReservoirIds.includes(v));
      });

    if (!wellPassingDirectories || !wellPassingDirectories.length) {
      console.error('Не удалось найти справочники проходки по заданным параметрам.');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT('Не удалось найти справочники проходки по заданным параметрам.');
      }

      return null;
    }

    if (wellPassingDirectories.length > 1) {
      console.error(
        `Found multiple active Common_WellPassing directories.
               Automatic passing value calculation will be disabled until this issue is fixed.`,
      );
      if (showNotifications) {
        this.notificationsService.showErrorMessageT(
          'Найдено несколько актуальных справочников проходки по скважине на основе заданных параметров. Необходимо отключить неактуальные справочники. Проходка не будет рассчитана.',
        );
      }

      return null;
    }

    const wellPassingDirectory = wellPassingDirectories[0];

    const passingByWellPurpose = wellPassingDirectory.data['passingByWellPurpose'];

    if (!Array.isArray(passingByWellPurpose)) {
      console.error('passingByWellPurpose is invalid');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT('Возникла ошибка при расчёте проходки');
      }

      return null;
    }

    const baseWellPassingValue = passingByWellPurpose.find(
      (passingObject) => parsePrimitivesFromString(passingObject['wellPurposeId']) === wellPurpose.value,
    )?.['passing'];

    if (!hasValue(baseWellPassingValue) || typeof baseWellPassingValue !== 'number') {
      console.error('Не удалось найти справочное значение проходки по заданным параметрам.');
      if (showNotifications) {
        this.notificationsService.showErrorMessageT(
          'Не удалось найти справочное значение проходки по заданным параметрам.',
        );
      }

      return null;
    }

    return baseWellPassingValue;
  }

  applyWinterCoefToDuration(duration: number, startDate: number): number {
    const endDate = startDate + convertHoursToSeconds(duration * 24);
    const winterCoefDirectory = this.directories
      .getObject('GOplan_Dict_WinterRatio', true)
      ?.find((dv): dv is TDictObject<{ startDate: number; endDate: number; ratio: number }> => {
        const WCStartDate = dv.data['startDate'];
        const WCEndDate = dv.data['endDate'];

        if (typeof WCStartDate !== 'number' || typeof WCEndDate !== 'number') {
          return false;
        }

        return (WCStartDate <= startDate && startDate <= WCEndDate) || (WCStartDate <= endDate && endDate <= WCEndDate);
      });

    if (!winterCoefDirectory) {
      return duration;
    }

    const winterIntervalDurationInSecs =
      Math.min(winterCoefDirectory.data.endDate, endDate) - Math.max(winterCoefDirectory.data.startDate, startDate);
    const winterIntervalDuration = Math.trunc((convertSecondsToHours(winterIntervalDurationInSecs) * 1000) / 24) / 1000;

    const nonWinterIntervalDuration = duration - winterIntervalDuration;

    return nonWinterIntervalDuration + winterIntervalDuration * winterCoefDirectory.data.ratio;
  }

  @action.bound
  getMainDataDirPassingValue(): number | null {
    const { rigOperationsService } = this.form;
    const {
      block,
      field,
      fluidType,
      wellType,
      wellPurpose,
      trunkLength: trunkLengthControl,
      pilotNumber: pilotControl,
      reservoir,
    } = this.form.fields;

    if (!trunkLengthControl) {
      console.error('trunkLengthControl is not presented');
      this.notificationsService.showErrorMessageT('Не удалось установить справочное значение');
      return null;
    }

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value) {
      return null;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    const wellPassingFilterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      year,
    };

    const wellPassingBySectionsFilterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      wellPurposeId: wellPurpose.value,
      year,
    };

    const wellPassingDirectories = this.rootStore.directories
      .getObject('Common_WellPassing', true, wellPassingFilterData)
      ?.filter((dv) => {
        const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
        const reservoirIds = reservoir.value as number[];

        if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
          return false;
        }

        return reservoirIds.every((v) => dvReservoirIds.includes(v));
      });

    if (!wellPassingDirectories || !wellPassingDirectories.length) {
      return null;
    }

    if (wellPassingDirectories.length > 1) {
      console.error(
        `Found multiple active Common_WellPassing directories.
           Automatic passing value calculation will be disabled until this issue is fixed.`,
      );
      return null;
    }

    const wellPassingDirectory = wellPassingDirectories[0];

    const passingByWellPurpose = wellPassingDirectory.data['passingByWellPurpose'];

    if (!Array.isArray(passingByWellPurpose)) {
      console.error('passingByWellPurpose is invalid');
      return null;
    }

    const baseWellPassingValue = passingByWellPurpose.find(
      (passingObject) => parsePrimitivesFromString(passingObject['wellPurposeId']) === wellPurpose.value,
    )?.['passing'];

    if (!hasValue(baseWellPassingValue) || typeof baseWellPassingValue !== 'number') {
      return null;
    }

    const trunkLength =
      hasValue(trunkLengthControl.value) && typeof trunkLengthControl.value === 'number' ? trunkLengthControl.value : 0;

    const extraPassingByPilots = ((): number => {
      if (pilotControl.isDisabled) {
        return 0;
      }

      if (!pilotControl || !hasValue(pilotControl.value) || typeof pilotControl.value !== 'number') {
        console.error('cannot find pilotControl');
        return 0;
      }

      const pilotsCount = pilotControl.value;

      if (!pilotsCount) {
        return 0;
      }

      const wellPassingBySectionsDirectories = this.rootStore.directories
        .getObject('Common_WellPassingBySection', true, wellPassingBySectionsFilterData)
        ?.filter((dv) => {
          const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
          const reservoirIds = reservoir.value as number[];

          if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
            return false;
          }

          return reservoirIds.every((v) => dvReservoirIds.includes(v));
        });

      if (!wellPassingBySectionsDirectories || !wellPassingBySectionsDirectories.length) {
        console.error('cannot find directory Common_WellPassingBySection with given field values');
        return 0;
      }

      if (wellPassingBySectionsDirectories.length > 1) {
        console.error(
          `Found multiple active Common_WellPassingBySection directories.
             Data on pilots will not be included in the calculation.`,
        );
        return 0;
      }

      const wellPassingBySectionsDirectory = wellPassingBySectionsDirectories[0];

      const appSettingsPilotSectionIdDirectory = this.rootStore.directories
        .getObject('GOplan_AppSettings')
        ?.find((dv) => dv.data['key'] === 'pilotSectionId');

      if (!appSettingsPilotSectionIdDirectory) {
        console.error(
          `cannot find app settings directory with sectionTypeId key.
             Data on pilots will not be included in the calculation.`,
        );
        return 0;
      }

      const pilotSectionId = appSettingsPilotSectionIdDirectory.data['value'];

      if (typeof pilotSectionId !== 'number') {
        console.error(
          'invalid pilotSectionId value. Data on pilots will not be included in the calculation.',
          pilotSectionId,
        );
        return 0;
      }

      const passingBySectionType = wellPassingBySectionsDirectory.data['passingBySection'];

      if (!Array.isArray(passingBySectionType)) {
        console.error('passingBySectionType is invalid');
        return 0;
      }

      const pilotSectionBasePassing = passingBySectionType.find(
        (passingObject) => parsePrimitivesFromString(passingObject['sectionTypeId']) === pilotSectionId,
      )?.['passing'];

      if (!hasValue(pilotSectionBasePassing) || typeof pilotSectionBasePassing !== 'number') {
        return 0;
      }

      return pilotsCount * pilotSectionBasePassing;
    })();

    const totalPassingValue = baseWellPassingValue + trunkLength + extraPassingByPilots;
    return totalPassingValue;
  }

  @action.bound
  getMainDataDirCommercialSpeed(): number | null {
    const rigOperation = this.form.rigOperationsService.rigOperations[0];
    const rigId = rigOperation?.rigId;
    const startDate = rigOperation?.stages.find((st) =>
      this.form.rigOperationsService.isDrillingStage(st.wellLifeCycleStageId),
    )?.startDate;

    if (!rigId || !startDate) {
      return null;
    }

    const commercialSpeed = this.getCommercialSpeedFromDirectories(rigId, startDate);

    if (!commercialSpeed) {
      return null;
    }

    return commercialSpeed;
  }

  @action.bound
  getDirStageDuration(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    showNotifications?: boolean,
  ): number | null {
    const rigId = rigOperation.rigId;

    if (!hasValue(rigId) || !hasValue(stage.wellLifeCycleStageId)) {
      return null;
    }

    const modelId = this.rootStore.directories.getObject('Common_Rig')?.find((rig) => rig.id === rigId)?.data[
      'modelId'
    ];

    if (!hasValue(modelId)) {
      return null;
    }

    const isDrillingStage = this.form.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId);

    if (!isDrillingStage) {
      const durationByStage = this.rootStore.directories
        .getObject('Common_StageDuration')
        ?.find((dv) => dv.data['modelId'] === modelId)?.data['durationByStage'];

      if (!durationByStage || !Array.isArray(durationByStage)) {
        return null;
      }

      const duration = durationByStage.find(
        (d) => parsePrimitivesFromString(d['wellLifeCycleStageId']) === stage.wellLifeCycleStageId,
      )?.['duration'];

      if (!hasValue(duration)) {
        return null;
      }

      return duration;
    } else {
      const stageStartDate = stage.startDate;

      if (!stageStartDate) {
        return null;
      }

      const commercialSpeed = (() => {
        if (!this.form.rigOperationsService.isSectionDetailingOrManyRigOperations) {
          const { commercialSpeed } = this.form.fields;

          return hasValue(commercialSpeed?.value) && typeof commercialSpeed?.value === 'number'
            ? commercialSpeed.value
            : null;
        } else {
          return this.getCommercialSpeedFromDirectories(
            rigOperation.rigId as number,
            stageStartDate,
            showNotifications,
          );
        }
      })();
      const passingValue = (() => {
        if (!this.form.rigOperationsService.isSectionDetailingOrManyRigOperations) {
          const { passingValue } = this.form.fields;

          return hasValue(passingValue?.value) && typeof passingValue?.value === 'number' ? passingValue.value : null;
        } else {
          return this.getDirPassingValue(stageStartDate, showNotifications);
        }
      })();

      if (!hasValue(commercialSpeed) || !hasValue(passingValue)) {
        if (showNotifications && !this.form.rigOperationsService.isSectionDetailingOrManyRigOperations) {
          this.notificationsService.showErrorMessageT(
            'Не заполнены поля проходки и/или коммерческой скорости. Приведение к справочным данным не может быть закончено',
          );
          return null;
        }

        // this.notificationsService.showErrorMessageT(
        //   // eslint-disable-next-line max-len
        //   'Отсутствуют необходимые справочные значения проходки и/или коммерческой скорости. Приведение к справочным данным не может быть закончено'
        // );
        return null;
      }

      const duration = this.applyWinterCoefToDuration(
        calculateDurationByCommercialSpeed(passingValue, commercialSpeed),
        stageStartDate,
      );

      return duration;
    }
  }

  @action.bound
  getDirStageCommercialSpeed(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    showNotifications?: boolean,
  ): number | null {
    if (!rigOperation.rigId || !stage.startDate) {
      return null;
    }

    const commercialSpeed = this.getCommercialSpeedFromDirectories(
      rigOperation.rigId,
      stage.startDate,
      showNotifications,
    );

    if (!commercialSpeed) {
      return null;
    }

    return commercialSpeed;
  }

  init = (): VoidFunction => {
    const stageCommercialSpeedDisposer = (() => {
      const mainDataFieldsDisposer = this.form.eventBus.subscribe('mainDataValueChange', async ({ fieldId }) => {
        if (!['block', 'field', 'fluidType', 'wellType', 'reservoir'].includes(fieldId)) {
          return;
        }

        this.form.rigOperationsService.rigOperations.forEach(async (rigOperation) => {
          const drillingStage = rigOperation.stages.find((stage) =>
            this.form.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId),
          );

          if (!drillingStage) {
            return;
          }

          await this.loadCommercialSpeedDirectory(rigOperation, drillingStage);
        });
      });
      const stageAndRigOperationValueChangeDisposer = this.form.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        async ({ fieldId, rigOperation, stage }) => {
          if (!['startDate', 'rigId'].includes(fieldId)) {
            return;
          }

          if (stage) {
            if (!this.form.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId)) {
              return;
            }

            await this.loadCommercialSpeedDirectory(rigOperation, stage);

            return;
          }

          rigOperation.stages.forEach(async (stage) => {
            await this.loadCommercialSpeedDirectory(rigOperation, stage);
          });
        },
      );
      const updatedStagesListDisposer = this.form.eventBus.subscribe('updatedStageList', ({ rigOperation, stages }) => {
        rigOperation.stages.forEach(async (stage) => {
          await this.loadCommercialSpeedDirectory(rigOperation, stage);
        });
      });

      return () => {
        mainDataFieldsDisposer();
        stageAndRigOperationValueChangeDisposer();
        updatedStagesListDisposer();
      };
    })();

    const mainDataPassingValueDisposer = (() => {
      const mainDataFieldsDisposer = this.form.eventBus.subscribe('mainDataValueChange', async ({ fieldId }) => {
        if (!['block', 'field', 'fluidType', 'wellType', 'wellPurpose', 'reservoir'].includes(fieldId)) {
          return;
        }

        const requests: Promise<unknown>[] = [];

        if (fieldId !== 'wellPurpose') {
          requests.push(this.loadWellPassingValueDirectory());
        }

        requests.push(this.loadPassingValueBySectionsDirectory());

        await Promise.all(requests);
      });
      const drillingStageStartDateDisposer = this.form.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        async ({ fieldId, stage }) => {
          const { rigOperations } = this.form.rigOperationsService;

          if (
            fieldId !== 'startDate' ||
            !stage ||
            stage.sections.length ||
            rigOperations.length > 1 ||
            !this.form.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId)
          ) {
            return;
          }

          await Promise.all([this.loadWellPassingValueDirectory(), this.loadPassingValueBySectionsDirectory()]);
        },
      );
      const deleteRigOperationDisposer = this.form.eventBus.subscribe('deleteRigOperation', async () => {
        const { rigOperations } = this.form.rigOperationsService;

        if (rigOperations.length > 1 || !rigOperations.length || !rigOperations[0]?.stages.length) {
          return;
        }

        const drillingStage = rigOperations[0]?.stages.find((s) =>
          this.form.rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
        );

        if (!drillingStage || !drillingStage.startDate) {
          return;
        }

        await Promise.all([this.loadWellPassingValueDirectory(), this.loadPassingValueBySectionsDirectory()]);
      });
      const updateStagesDisposer = this.form.eventBus.subscribe('updatedStageList', async () => {
        const { rigOperations } = this.form.rigOperationsService;

        if (rigOperations.length > 1 || !rigOperations.length || !rigOperations[0]?.stages.length) {
          return;
        }

        const drillingStage = rigOperations[0]?.stages.find((s) =>
          this.form.rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
        );

        if (!drillingStage || !drillingStage.startDate) {
          return;
        }

        await Promise.all([this.loadWellPassingValueDirectory(), this.loadPassingValueBySectionsDirectory()]);
      });

      return () => {
        mainDataFieldsDisposer();
        drillingStageStartDateDisposer();
        deleteRigOperationDisposer();
        updateStagesDisposer();
      };
    })();

    const mainDataCommercialSpeedDisposer = (() => {
      const mainDataFieldsDisposer = this.form.eventBus.subscribe('mainDataValueChange', async ({ fieldId }) => {
        if (!['block', 'field', 'fluidType', 'wellType', 'reservoir'].includes(fieldId)) {
          return;
        }

        const {
          rigOperationsService: { rigOperations, isDrillingStage },
        } = this.form;

        if (!rigOperations.length) {
          return;
        }

        const drillingStage = rigOperations[0]?.stages.find((stage) => isDrillingStage(stage.wellLifeCycleStageId));

        if (!drillingStage) {
          return;
        }

        await this.loadCommercialSpeedDirectory(rigOperations[0], drillingStage);
      });
      const stageAndRigOperationValueChangeDisposer = this.form.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        async ({ fieldId }) => {
          if (!['startDate', 'rigId'].includes(fieldId)) {
            return;
          }

          const {
            rigOperationsService: { rigOperations, isDrillingStage },
          } = this.form;

          if (!rigOperations.length) {
            return;
          }

          const drillingStage = rigOperations[0]?.stages.find((stage) => isDrillingStage(stage.wellLifeCycleStageId));

          if (!drillingStage) {
            return;
          }

          await this.loadCommercialSpeedDirectory(rigOperations[0], drillingStage);
        },
      );
      const updateStagesDisposer = this.form.eventBus.subscribe('updatedStageList', async () => {
        const {
          rigOperationsService: { rigOperations, isDrillingStage },
        } = this.form;

        if (!rigOperations.length) {
          return;
        }

        const drillingStage = rigOperations[0]?.stages.find((stage) => isDrillingStage(stage.wellLifeCycleStageId));

        if (!drillingStage) {
          return;
        }

        await this.loadCommercialSpeedDirectory(rigOperations[0], drillingStage);
      });

      return () => {
        mainDataFieldsDisposer();
        stageAndRigOperationValueChangeDisposer();
        updateStagesDisposer();
      };
    })();

    return () => {
      stageCommercialSpeedDisposer();
      mainDataPassingValueDisposer();
      mainDataCommercialSpeedDisposer();
    };
  };
}
